/**
 * Dev-only AuthClient used with the local backend (packages/local-backend).
 *
 * Active ONLY when the SPA is built with `VITE_DEV_USER` set (never set in
 * any deployed environment). The real authentication happens server-side:
 * the local backend trusts the `x-dev-user` header that the Apollo link adds
 * under the same flag, and resolves the principal's roles/permissions from
 * the seeded DB. This client only satisfies the SPA's session shell so the
 * app renders without the auth.molaris.ai OIDC round-trip, which does not
 * exist locally.
 */
import { loadAuthConfig } from "./config.js";
import type { AuthClient, CreateAuthClientOptions } from "./client.js";
import type { SignOutResult } from "./sign-out.js";
import type { Session, SessionListener, AuthErrorListener, Tenant } from "./types.js";

const DEV_TENANT: Tenant = {
  id: "11111111-1111-1111-1111-111111111111",
  name: "Demo Dental Lab (local)",
  slug: "demo",
};

export function createDevAuthClient(
  devUser: string,
  options: CreateAuthClientOptions = {},
): AuthClient {
  const now = Math.floor(Date.now() / 1000);
  const session: Session = {
    userGlobalId: `dev-${devUser}`,
    userLocalId: `dev-${devUser}`,
    userTenantId: DEV_TENANT.id,
    email: devUser,
    fullName: devUser.split("@")[0] ?? devUser,
    internalRoleIds: [],
    orgUnitScopeIds: [],
    productEntitlements: ["clearpath"],
    mfaVerified: true,
    mfaVerifiedAt: new Date().toISOString(),
    portalRole: null,
    isJitGrant: false,
    jitGrantId: null,
    sessionId: "dev-local-session",
    issuedAt: now,
    expiresAt: now + 12 * 60 * 60,
  };

  const sessionListeners = new Set<SessionListener>();
  const errorListeners = new Set<AuthErrorListener>();

  return {
    async signIn() {
      // Already "signed in"; nothing to redirect to locally.
    },
    async handleCallback() {
      return session;
    },
    async getAccessToken() {
      // No token locally — the Apollo link's x-dev-user header carries auth.
      return null;
    },
    async getCurrentUser() {
      return session;
    },
    async signOut(): Promise<SignOutResult> {
      // No server session to revoke and no /userinfo endpoint locally, so
      // there is nothing to probe; reload lands back on the dev session.
      window.location.assign("/");
      return { verdict: "signed_out", probe: "not_run" };
    },
    async switchTenant() {
      return session;
    },
    async refreshSession() {
      // Dev sessions are static; there is no server session to re-mint.
      return session;
    },
    async listTenantMemberships() {
      return [DEV_TENANT];
    },
    onSessionChange(listener) {
      sessionListeners.add(listener);
      // Fire immediately so providers waiting on a session settle at once.
      listener(session);
      return () => sessionListeners.delete(listener);
    },
    onAuthError(listener) {
      errorListeners.add(listener);
      return () => errorListeners.delete(listener);
    },
    emitAuthError(error) {
      for (const l of errorListeners) l(error ?? { code: "unknown", message: "dev auth error" });
    },
    config: loadAuthConfig(options),
  };
}
